import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Notice, StatusPill } from "../../design/components";
import { useAspBreakerControl, useAspConnection } from "../../api/queries";
import { dateTime, relative } from "../../lib/format";
import type { DashboardSummary } from "../../api/types";
import styles from "./GroupDashboard.module.css";

const IN_FLIGHT_QUEUE = "/documents?state=in_flight";
const ATTENTION_AFTER_MINUTES = 15;

function stamp(iso: string | null | undefined): string {
  return iso ? dateTime(iso) : "—";
}

/** HUB-I06: a paused sender is worth a line even while the connection itself still
 * answers pings — `breaker` and `connection` can disagree on purpose. */
function BreakerNotice({ breaker }: { breaker: DashboardSummary["aspBreaker"] }) {
  if (breaker.state === "closed") return null;
  const openedMinutesAgo = breaker.openedAt
    ? (Date.now() - new Date(breaker.openedAt).getTime()) / 60000
    : 0;
  const longRunning = openedMinutesAgo > ATTENTION_AFTER_MINUTES;
  const label =
    breaker.state === "half_open"
      ? "Service provider: testing whether it is back"
      : `Service provider: paused since ${stamp(breaker.openedAt)}`;
  const next = breaker.retryAfterUntil
    ? `, next probe ${stamp(breaker.retryAfterUntil)} (${relative(breaker.retryAfterUntil)})`
    : "";
  return (
    <Notice kind={longRunning ? "fail" : "info"}>
      {label}
      {next}
      {longRunning ? " — this has been going on a while." : ""}
    </Notice>
  );
}

/** HUB-I08: forcing the breaker open/resuming it — an operator override, independent of
 * the ordinary failure count. Resume always re-enters through half_open, never straight
 * to closed. */
function BreakerControl({ breaker }: { breaker: DashboardSummary["aspBreaker"] }) {
  const control = useAspBreakerControl();

  return (
    <div className={styles.breakerControl}>
      {control.isError ? (
        <Notice kind="fail">
          {breaker.forcedOpen ? "Could not resume the service provider." : "Could not pause the service provider."}
        </Notice>
      ) : null}
      <button
        type="button"
        className="btn btn-sm"
        disabled={control.isPending}
        onClick={() => control.mutate(breaker.forcedOpen ? "resume" : "pause")}
      >
        {breaker.forcedOpen ? "Resume sending" : "Pause sending"}
      </button>
    </div>
  );
}

export function AspHealthCard({
  connection,
  breaker,
}: {
  connection: DashboardSummary["aspConnection"];
  breaker: DashboardSummary["aspBreaker"];
}) {
  const { t } = useTranslation();
  const live = useAspConnection().data;
  // The summary's probe cache is the same source, so the two surfaces cannot disagree.
  const reachable = live?.reachable ?? connection.reachable;
  const checkedAt = live?.checkedAt ?? connection.checkedAt;

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="grow">
          <Link className="link" to={IN_FLIGHT_QUEUE}>
            {t("dashboard.aspHealthCard.title", "Service provider connection")}
          </Link>
        </h2>
        <StatusPill
          status={reachable ? "connected" : "failed"}
          label={
            reachable
              ? t("dashboard.aspHealthCard.connected", "Connected")
              : t("dashboard.aspHealthCard.unreachable", "Unreachable")
          }
        />
      </div>
      <div className="card-body">
        <BreakerNotice breaker={breaker} />
        <BreakerControl breaker={breaker} />
        <p className={styles.aspNote}>
          {t(
            "dashboard.aspHealthCard.note",
            "The service provider validates the document and reports it to the Oman Tax Authority.",
          )}
        </p>
        <dl className={styles.facts}>
          <dt>{t("dashboard.aspHealthCard.lastChecked", "Last checked")}</dt>
          <dd>{stamp(checkedAt)}</dd>
          <dt>{t("dashboard.aspHealthCard.detail", "Detail")}</dt>
          <dd>{connection.detail ?? "—"}</dd>
          <dt>{t("dashboard.aspHealthCard.lastSuccessfulExchange", "Last successful exchange")}</dt>
          <dd>{stamp(live?.lastSuccessAt)}</dd>
          <dt>{t("dashboard.aspHealthCard.documentsAwaitingOutcome", "Documents awaiting an outcome")}</dt>
          <dd>{live ? String(live.inFlightCount) : "—"}</dd>
          {reachable ? null : (
            <>
              <dt>{t("dashboard.aspHealthCard.lastFailure", "Last failure")}</dt>
              <dd>{stamp(live?.lastFailureAt)}</dd>
              <dt>{t("dashboard.aspHealthCard.failureDetail", "Failure detail")}</dt>
              <dd>{live?.lastFailureDetail ?? "—"}</dd>
            </>
          )}
        </dl>
      </div>
    </section>
  );
}
